import config from '../../config/config'


export class HttpError extends Error {
  constructor(message, status, url, body) {
    super(message);
    this.name = 'HttpError';
    this.status = status;
    this.url = url;
    this.body = body;
  }
}

export class HttpClient {
  constructor(opts) {
    this.baseURL = (opts.baseURL || '').replace(/\/$/, '');
    this.credentials = opts.credentials || 'include';
    this.defaultHeaders = {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      'client-id': config.VASLOLA_AUTH_CLIENT_ID,
      ...(opts.defaultHeaders || {}),
    };
    this.timeoutMs = opts.timeoutMs != null ? opts.timeoutMs : 6000;
    this.retries = Math.max(0, opts.retries != null ? opts.retries : 1);
    this.getAccessToken = opts.getAccessToken;
  }

  withTimeout(promise, ms) {
    return new Promise((resolve, reject) => {
      const id = setTimeout(() => reject(new Error(`Request timeout after ${ms}ms`)), ms);
      promise.then(
        (res) => { clearTimeout(id); resolve(res); },
        (err) => { clearTimeout(id); reject(err); }
      );
    });
  }

  async doFetch(path, init) {
    const url = `${this.baseURL}${path.startsWith('/') ? '' : '/'}${path}`;

    // Fusionamos headers
    const headers = { ...this.defaultHeaders, ...(init.headers || {}) };

    // DETECCIÓN INTELIGENTE DE BODY
    // Si el body es FormData, ELIMINAMOS 'Content-Type'.
    // El navegador debe ponerlo automáticamente con el "boundary" correcto.
    if (init.body instanceof FormData) {
      delete headers['Content-Type'];
    }

    // Authorization
    const at = this.getAccessToken && this.getAccessToken();
    if (at) headers['Authorization'] = `Bearer ${at}`;

    const req = { ...init, headers, credentials: this.credentials };

    const attempt = async () => fetch(url, req);

    let lastError;
    const max = this.retries + 1;
    for (let i = 0; i < max; i++) {
      try {
        const res = await this.withTimeout(attempt(), this.timeoutMs);
        return res;
      } catch (err) {
        lastError = err;
        // Exponential backoff
        await new Promise((r) => setTimeout(r, Math.pow(2, i) * 150));
      }
    }
    throw lastError;
  }

  async request(method, path, body, extra = {}) {
    // Extraemos responseType para saber cómo procesar la respuesta (json, text, blob)
    const { responseType, ...fetchOptions } = extra;

    // Si el método es GET o HEAD, forzamos el body a undefined,
    // sin importar qué nos envíen. Fetch explotará si recibe body en GET.
    const isReadOnly = ['GET', 'HEAD'].includes(method.toUpperCase());

    // Lógica de Body:
    // 1. Si es GET/HEAD -> undefined
    // 2. Si es binario (FormData/Blob) -> lo pasamos tal cual
    // 3. Si es otro método (POST/PUT) y es objeto -> JSON.stringify
    let payload;

    if (isReadOnly) {
      payload = undefined;
    } else {
      const isBinary = (body instanceof FormData) || (body instanceof Blob);
      payload = (body !== undefined && !isBinary) ? JSON.stringify(body) : body;
    }

    const res = await this.doFetch(path, {
      method,
      ...fetchOptions,
      body: payload,
    });

    // PARSING DE RESPUESTA
    const parse = async () => {
      // 1. Si pedimos explícitamente un blob (ej: descargar PDF)
      if (responseType === 'blob') return res.blob();

      // 2. Detección automática standard
      const contentType = res.headers.get('content-type') || '';
      if (contentType.includes('application/json')) return res.json();
      return res.text();
    };

    if (!res.ok) {
      // Si falla, intentamos leer el error, si es un blob intentamos leerlo como texto/json para el error
      let payloadError;
      try {
        // A veces el servidor devuelve error en JSON aunque esperábamos Blob
        const contentType = res.headers.get('content-type') || '';
        payloadError = contentType.includes('application/json') ? await res.json() : await res.text();
      } catch (e) {
        payloadError = undefined;
      }

      throw new HttpError(`HTTP ${res.status}`, res.status, res.url, payloadError);
    }

    return parse();
  }

  get(path, extra) { return this.request('GET', path, undefined, extra); }
  post(path, body, extra) { return this.request('POST', path, body, extra); }
  put(path, body, extra) { return this.request('PUT', path, body, extra); }
  patch(path, body, extra) { return this.request('PATCH', path, body, extra); }
  delete(path, extra) { return this.request('DELETE', path, undefined, extra); }
}